import axios from 'axios';
import { supabase } from '../lib/supabase';
import categoryPreflightResolver from './categoryPreflightResolver.cjs';
import publishProductRequest from './publishProductRequest.cjs';

const {
  resolvePreflightCategory,
  markPreflightCategory,
  isPreflightCategoryResolved
} = categoryPreflightResolver;
const { postPublishProductRequest } = publishProductRequest;

const ATTRIBUTE_PREVIEW_MAX_REQUEST_BYTES = 256 * 1024;
const ATTRIBUTE_PREVIEW_OMITTED_SCALAR_FIELDS = new Set([
  'description', 'descripción', 'descripcion',
  'images', 'image', 'imagenes', 'imágenes', 'imágenes de alta resolución', 'pictures',
  'url', 'url de producto no normalizada', 'texto de ubicación',
  'reseñas de la página del producto', 'información importante'
]);
const ATTRIBUTE_PREVIEW_STRUCTURED_FIELDS = [
  'productOverview', 'specifications', 'technicalSpecifications', 'technicalDetails',
  'details', 'itemDetails', 'specs', 'productDetails', 'Especificaciones técnicas',
  'Características técnicas'
];
const ATTRIBUTE_PREVIEW_VALUE_KEYS = [
  'id', 'key', 'name', 'label', 'property', 'attribute',
  'value_id', 'valueId', 'value_name', 'valueName', 'value', 'content'
];

const isPreviewScalar = (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value);

const compactAttributeValue = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  return ATTRIBUTE_PREVIEW_VALUE_KEYS.reduce((compact, key) => {
    if (isPreviewScalar(value[key])) compact[key] = value[key];
    return compact;
  }, {});
};

const compactStructuredAttributeValue = (value) => {
  if (Array.isArray(value)) return value.map(compactStructuredAttributeValue);
  if (!value || typeof value !== 'object') return value;

  const compact = ATTRIBUTE_PREVIEW_VALUE_KEYS.reduce((result, key) => {
    if (isPreviewScalar(value[key])) result[key] = value[key];
    return result;
  }, {});

  if (Object.keys(compact).length > 0) return compact;
  return Object.entries(value).reduce((result, [key, entryValue]) => {
    if (isPreviewScalar(entryValue)) result[key] = entryValue;
    return result;
  }, {});
};

const compactProductForAttributePreview = (product) => {
  const compact = {};
  Object.entries(product || {}).forEach(([key, value]) => {
    if (!isPreviewScalar(value) || ATTRIBUTE_PREVIEW_OMITTED_SCALAR_FIELDS.has(String(key).toLowerCase())) return;
    compact[key] = value;
  });

  // MCO_PREVIEW_DESCRIPTION_V1
  // Conservar evidencia para validar atributos en cualquier categoría MCO.
  const sourceDescription = [
    product?.description,
    product?.descripcion,
    product?.['descripción']
  ].find(value =>
    typeof value === 'string' && value.trim()
  );

  if (sourceDescription) {
    compact.description = sourceDescription.slice(0, 2500);
  }

  const explicitAttributes = Array.isArray(product?.attributes)
    ? product.attributes
    : (Array.isArray(product?.atributos) ? product.atributos : null);
  if (explicitAttributes) {
    compact.attributes = explicitAttributes.map(compactAttributeValue);
  }

  ATTRIBUTE_PREVIEW_STRUCTURED_FIELDS.forEach((field) => {
    if (product?.[field] !== undefined) compact[field] = compactStructuredAttributeValue(product[field]);
  });

  ['features', 'características'].forEach((field) => {
    if (product?.[field] !== undefined) compact[field] = product[field];
  });

  return compact;
};

const getUtf8Size = (value) => new TextEncoder().encode(value).byteLength;

const buildAttributePreviewRequest = (items, accessToken) => {
  const body = JSON.stringify({
    products: items.map(item => item.product),
    accessToken,
    previewAttributes: true
  });
  return { items, body, bytes: getUtf8Size(body) };
};

const getPreviewFieldSizes = (product) => Object.entries(product || {})
  .map(([field, value]) => ({ field, bytes: getUtf8Size(JSON.stringify({ [field]: value })) }))
  .sort((left, right) => right.bytes - left.bytes)
  .slice(0, 3);

const createPreviewError = (item, code, message, requestBytes, extra = {}) => ({
  id: item?.product?.id ?? null,
  asin: item?.product?.asin || item?.product?.ASIN || null,
  category_id: item?.categoryResolution?.categoryId || item?.product?.category_id || null,
  categoryResolution: item?.categoryResolution,
  attributePreparation: null,
  preparationError: { code, message, requestBytes, ...extra }
});

const splitPreviewBatch = (batch, accessToken) => {
  const midpoint = Math.ceil(batch.items.length / 2);
  return [
    buildAttributePreviewRequest(batch.items.slice(0, midpoint), accessToken),
    buildAttributePreviewRequest(batch.items.slice(midpoint), accessToken)
  ];
};

const splitAttributePreviewRequests = (items, accessToken) => {
  const batches = [];
  let currentBatch = [];

  items.forEach((item) => {
    const nextBatch = [...currentBatch, item];
    const singleItemBatch = buildAttributePreviewRequest([item], accessToken);
    if (singleItemBatch.bytes > ATTRIBUTE_PREVIEW_MAX_REQUEST_BYTES) {
      if (currentBatch.length > 0) batches.push(buildAttributePreviewRequest(currentBatch, accessToken));
      batches.push({ ...singleItemBatch, oversized: true });
      currentBatch = [];
      return;
    }
    if (currentBatch.length > 0 && buildAttributePreviewRequest(nextBatch, accessToken).bytes > ATTRIBUTE_PREVIEW_MAX_REQUEST_BYTES) {
      batches.push(buildAttributePreviewRequest(currentBatch, accessToken));
      currentBatch = [item];
      return;
    }
    currentBatch = nextBatch;
  });

  if (currentBatch.length > 0) batches.push(buildAttributePreviewRequest(currentBatch, accessToken));
  return batches;
};

// MercadoLibre API Service for Real Publishing
class MercadoLibreService {
  constructor() {
    this.apiUrl = import.meta.env?.VITE_MERCADOLIBRE_API_URL || 'https://api.mercadolibre.com';
    this.authUrl = import.meta.env?.VITE_MERCADOLIBRE_AUTH_URL || 'https://auth.mercadolibre.com.co';
    this.appId = import.meta.env?.VITE_MERCADOLIBRE_APP_ID;
    this.clientId = import.meta.env?.VITE_MERCADOLIBRE_CLIENT_ID || import.meta.env?.VITE_MERCADOLIBRE_APP_ID;
    this.clientSecret = import.meta.env?.VITE_MERCADOLIBRE_CLIENT_SECRET;
    this.redirectUri = import.meta.env?.VITE_MERCADOLIBRE_REDIRECT_URI || 'https://mercadobulk.com/oauth/callback.html';
    this.siteId = import.meta.env?.VITE_MERCADOLIBRE_SITE_ID || 'MCO';
    this.defaultCategoryId = import.meta.env?.VITE_MERCADOLIBRE_FALLBACK_CATEGORY_ID || 'MCO1430';
    this.categoryCache = new Map();
    this.categoryDiscoveryCache = new Map();
    this.categoryLeafStatus = new Map();
    this.categoryLeafStatus.set('MCO1000', false);
    this.knownNonLeafCategories = new Set(['MCO1000']);
    this.categoryDetailCache = new Map();
    this.userShippingCache = null;
    this.apiBaseUrl = this.resolveApiBaseUrl();
    
    // Create axios instance with default configuration
    this.api = axios?.create({
      baseURL: this.apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

  }

  resolveApiBaseUrl() {
    const envUrl = import.meta.env?.VITE_API_BASE_URL;
    const isLocalEnv = typeof envUrl === 'string' && /localhost|127\.0\.0\.1/i.test(envUrl);
    if (typeof window !== 'undefined') {
      if (!isLocalEnv && envUrl) {
        return envUrl;
      }
      return window.location.origin;
    }
    return (!isLocalEnv && envUrl) ? envUrl : 'https://mercadobulk.com';
  }

  // Get user information
  async getUserInfo() {
    try {
      const userId = this.getUserId();
      if (!userId) throw new Error('No user ID found');

      const apiBaseUrl = this.apiBaseUrl;
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      return {
        success: true,
        user: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to get user info'
      };
    }
  }

  // Get categories for site
  async getCategories() {
    try {
      const apiBaseUrl = this.apiBaseUrl;
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/categories/${this.siteId}`, {
        headers: { 'Authorization': `Bearer ${this.getAccessToken()}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      return {
        success: true,
        categories: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to get categories'
      };
    }
  }

  // Obtener árbol completo de categorías de MercadoLibre Colombia vía backend proxy
  async getCategoryTree(levels = Infinity) {
    const maxDepth = Number.isFinite(levels) ? levels : Infinity;
    const cacheKey = `ml_categories_${this.siteId}_${maxDepth === Infinity ? 'all' : maxDepth}`;
    try {
      const apiBaseUrl = this.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
      // 0) Cache local
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length) {
            return { success: true, categories: parsed, from: 'cache' };
          }
        }
      } catch {}
      // 0.5) Intentar volcado completo categories-all (flatten) si backend lo expone
      try {
        const allRes = await fetch(`${apiBaseUrl}/api/mercadolibre/categories-all/${this.siteId}`);
        if (allRes.ok) {
          const allJson = await allRes.json();
          const flat = Array.isArray(allJson?.data) ? allJson.data : [];
          if (flat.length) {
            const normalized = flat.map(cat => {
              const name = typeof cat?.name === 'string'
                ? cat.name.trim()
                : (typeof cat?.description === 'string' ? cat.description.trim() : String(cat?.id || '').trim());
              const pathSegments = typeof cat?.path === 'string'
                ? cat.path.split('>').map(segment => segment?.trim()).filter(Boolean)
                : (Array.isArray(cat?.path_from_root)
                    ? cat.path_from_root.map(item => item?.name).filter(Boolean)
                    : [name].filter(Boolean));
              const normalizedPath = pathSegments.length ? pathSegments.join(' > ') : name;
              return {
                ...cat,
                name,
                path: normalizedPath,
                leaf: cat?.leaf === true
              };
            });

            let filtered = normalized;
            if (Number.isFinite(maxDepth)) {
              filtered = normalized.filter(cat => {
                const depth = (cat?.path || '').split(' > ').filter(Boolean).length;
                return depth <= maxDepth;
              });
            }

            filtered.sort((a, b) => (a.path || '').localeCompare(b.path || ''));
            try { localStorage.setItem(cacheKey, JSON.stringify(filtered)); } catch {}
            for (const c of filtered) {
              if (c?.id) this.categoryLeafStatus.set(c.id, !!c.leaf);
              if (c?.id && c.leaf === false) this.knownNonLeafCategories.add(c.id);
            }
            return { success: true, categories: filtered, from: 'categories-all' };
          }
        }
      } catch (e) {
        console.warn('categories-all fallback no disponible:', e?.message);
      }
      // Top-level categories (proxy backend)
      const topRes = await fetch(`${apiBaseUrl}/api/mercadolibre/categories/${this.siteId}`, {
        headers: { 'Authorization': `Bearer ${this.getAccessToken()}` }
      });
      if (!topRes.ok) {
        const err = await topRes.text();
        throw new Error(`Top categories HTTP ${topRes.status}: ${err}`);
      }
      const topJson = await topRes.json();
      const top = Array.isArray(topJson?.data) ? topJson.data : (Array.isArray(topJson) ? topJson : []);

      const results = [];
      const queue = [];
      const visited = new Set();

      for (const parent of top) {
        if (parent?.id) {
          queue.push({ id: parent.id, name: parent.name, level: 1, path: parent.name });
        }
      }

      while (queue.length) {
        const node = queue.shift();
        if (!node?.id || visited.has(node.id)) continue;
        visited.add(node.id);

        const entry = {
          id: node.id,
          name: node.name || node.id,
          path: node.path || node.name || node.id,
          leaf: true
        };
        results.push(entry);
        this.categoryLeafStatus.set(entry.id, true);

        if (node.level >= maxDepth) continue;

        try {
          const detailRes = await fetch(`${apiBaseUrl}/api/mercadolibre/category/${node.id}`, {
            headers: { 'Authorization': `Bearer ${this.getAccessToken()}` }
          });
          if (!detailRes.ok) throw new Error(`HTTP ${detailRes.status}`);
          const detailJson = await detailRes.json();
          const children = detailJson?.data?.children_categories || [];

          if (Array.isArray(children) && children.length) {
            entry.leaf = false;
            this.categoryLeafStatus.set(entry.id, false);
            this.knownNonLeafCategories.add(entry.id);
            for (const child of children) {
              const childId = typeof child === 'object' ? child.id : child;
              const childName = typeof child === 'object' ? child.name : String(child);
              if (!childId || visited.has(childId)) continue;
              queue.push({
                id: childId,
                name: childName,
                level: node.level + 1,
                path: `${entry.path} > ${childName}`
              });
              if (child && typeof child === 'object' && Array.isArray(child.children_categories) && child.children_categories.length) {
                this.categoryLeafStatus.set(childId, false);
                this.knownNonLeafCategories.add(childId);
              }
            }
          }
        } catch (e) {
          console.warn('No se pudieron cargar subcategorías para', node.id, e?.message || e);
        }
      }

      if (results.length > 1) {
        results.sort((a, b) => (a.path || '').localeCompare(b.path || ''));
      }

      if (results.length) {
        try { localStorage.setItem(cacheKey, JSON.stringify(results)); } catch {}
      }

      return { success: true, categories: results, from: 'backend' };
    } catch (error) {
      console.error('getCategoryTree error (backend proxy):', error?.message || error);
      // 1) Fallback: llamada directa a API pública de ML (puede fallar por CORS en algunos entornos)
      try {
        const topRes = await fetch(`https://api.mercadolibre.com/sites/${this.siteId}/categories`);
        if (topRes.ok) {
          const top = await topRes.json();
          const queue = [];
          const visited = new Set();
          const results = [];

          if (Array.isArray(top)) {
            for (const cat of top) {
              if (!cat?.id) continue;
              queue.push({ id: cat.id, name: cat.name, level: 1, path: cat.name });
            }
          }

          while (queue.length) {
            const node = queue.shift();
            if (!node?.id || visited.has(node.id)) continue;
            visited.add(node.id);

            const entry = {
              id: node.id,
              name: node.name || node.id,
              path: node.path || node.name || node.id,
              leaf: true
            };
            results.push(entry);
            this.categoryLeafStatus.set(entry.id, true);

            if (node.level >= maxDepth) continue;

            try {
              const detailRes = await fetch(`https://api.mercadolibre.com/categories/${node.id}`);
              if (!detailRes.ok) throw new Error(`HTTP ${detailRes.status}`);
              const detail = await detailRes.json();
              const children = Array.isArray(detail?.children_categories) ? detail.children_categories : [];
              if (Array.isArray(detail?.path_from_root)) {
                const pathParts = detail.path_from_root
                  .map(item => item?.name)
                  .filter(Boolean);
                if (pathParts.length) {
                  entry.path = pathParts.join(' > ');
                }
              }

              if (children.length) {
                entry.leaf = false;
                this.categoryLeafStatus.set(entry.id, false);
                this.knownNonLeafCategories.add(entry.id);
                for (const child of children) {
                  const childId = typeof child === 'object' ? child.id : child;
                  const childName = typeof child === 'object' ? child.name : String(child);
                  if (!childId || visited.has(childId)) continue;
                  const childPath = entry.path ? `${entry.path} > ${childName}` : childName;
                  queue.push({ id: childId, name: childName, level: node.level + 1, path: childPath });
                  if (child && typeof child === 'object' && Array.isArray(child.children_categories) && child.children_categories.length) {
                    this.categoryLeafStatus.set(childId, false);
                    this.knownNonLeafCategories.add(childId);
                  }
                }
              }
            } catch (innerError) {
              console.warn('No se pudo obtener detalle de categoría (fallback directo):', node.id, innerError?.message || innerError);
            }
          }

          const onlyLeaves = results.filter(item => item.leaf === true);
          if (onlyLeaves.length > 1) {
            onlyLeaves.sort((a, b) => (a.path || '').localeCompare(b.path || ''));
          }

          if (onlyLeaves.length) {
            try { localStorage.setItem(cacheKey, JSON.stringify(onlyLeaves)); } catch {}
            return { success: true, categories: onlyLeaves, from: 'direct-tree' };
          }
        }
      } catch (e) {
        console.warn('Fallback directo a ML falló:', e?.message);
      }

      // 2) Último recurso: lista por defecto mínima para no bloquear la UI
      const defaults = [
        { id: 'MCO1051', name: 'Celulares y Teléfonos', path: 'Celulares y Teléfonos' },
        // Nota: MCO1648 usado en este entorno para Electrodomésticos y Aires Ac.
        { id: 'MCO1648', name: 'Electrodomésticos y Aires Ac.', path: 'Electrodomésticos y Aires Ac.' },
        { id: 'MCO1367', name: 'Hogar, Muebles y Jardín', path: 'Hogar, Muebles y Jardín' },
        { id: 'MCO1276', name: 'Deportes y Fitness', path: 'Deportes y Fitness' },
        { id: 'MCO1132', name: 'Juegos y Juguetes', path: 'Juegos y Juguetes' },
        { id: 'MCO1430', name: 'Ropa y Accesorios', path: 'Ropa y Accesorios' },
        { id: 'MCO1953', name: 'Salud y Equipamiento Médico', path: 'Salud y Equipamiento Médico' }
      ];
      defaults.forEach(cat => this.categoryLeafStatus.set(cat.id, true));
      return { success: true, categories: defaults, from: 'defaults' };
    }
  }

  async getCategoryChildren(categoryId) {
    try {
      if (!categoryId) {
        throw new Error('categoryId requerido');
      }
      const apiBaseUrl = this.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/category/${categoryId}`, {
        headers: { 'Authorization': `Bearer ${this.getAccessToken()}` }
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      const payload = await response.json().catch(() => ({}));
      const detail = payload?.data || payload || {};
      const children = Array.isArray(detail?.children_categories) ? detail.children_categories : [];
      return {
        success: true,
        detail,
        children
      };
    } catch (error) {
      console.error('Error obteniendo subcategorías:', error?.message || error);
      return {
        success: false,
        error: error?.message || 'No se pudieron cargar las subcategorías'
      };
    }
  }

  // Get category attributes
  async getCategoryAttributes(categoryId) {
    try {
      const response = await this.api?.get(`/categories/${categoryId}/attributes`, {
        headers: { 'Authorization': `Bearer ${this.getAccessToken()}` }
      });
      return {
        success: true,
        attributes: response?.data
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to get category attributes'
      };
    }
  }

  // MCO_CATALOG_BULK_ENRICH_V1
  async collectCatalogAttributesForBulk(products) {
    const unique = new Map();
    const slimSource = source => {
      if (Array.isArray(source)) {
        return source.slice(0, 25).map(row => {
          if (!row || typeof row !== 'object') return null;
          return Object.fromEntries(
            Object.entries(row)
              .filter(([, value]) =>
                ['string', 'number', 'boolean'].includes(typeof value)
              )
              .slice(0, 8)
              .map(([key, value]) =>
                [key, String(value).slice(0, 220)]
              )
          );
        }).filter(Boolean);
      }

      if (source && typeof source === 'object') {
        return Object.fromEntries(
          Object.entries(source)
            .filter(([, value]) =>
              ['string', 'number', 'boolean'].includes(typeof value)
            )
            .slice(0, 25)
            .map(([key, value]) =>
              [key, String(value).slice(0, 220)]
            )
        );
      }

      return null;
    };



    const asinOf = p => String(
      p?.asin || p?.ASIN || p?.amazon_asin ||
      p?.originalAsin || ''
    ).trim().toUpperCase();

    for (const product of products || []) {
      const asin = asinOf(product);
      const category_id = String(product?.category_id || '').trim();

      if (!asin || !/^MCO\d+$/.test(category_id)) continue;

      const key = `${asin}|${category_id}`;
      if (unique.has(key)) continue;

      const attrs = Array.isArray(product?.attributes)
        ? product.attributes
        : Array.isArray(product?.atributos)
          ? product.atributos
          : [];

      const identifiers = attrs.filter(a =>
        /gtin|ean|upc|brand|marca|model|modelo|part.?number|global.trade/i.test(
          String(a?.id || a?.key || a?.name || a?.label || '')
        )
      ).slice(0, 30).map(a => ({
        id: a.id,
        key: a.key,
        name: a.name,
        value: a.value,
        value_name: a.value_name,
        value_id: a.value_id
      }));

      unique.set(key, {
        asin,
        category_id,
        title: String(product?.title || product?.titulo || '').slice(0, 160),
        brand: product?.brand || product?.marca || '',
        model: product?.model || product?.modelName || '',
        manufacturerPartNumber:
          product?.manufacturerPartNumber || product?.partNumber || '',
        gtin: product?.gtin || '',
        ean: product?.ean || '',
        upc: product?.upc || '',
        attributes: identifiers,
        description: String(
          product?.description || product?.descripcion || ''
        ).slice(0, 2500),
        features: (
          Array.isArray(product?.features) ? product.features : []
        ).slice(0, 8).map(value => String(value || '').slice(0, 400)),
        productOverview: slimSource(product?.productOverview),
        specifications: slimSource(product?.specifications),
        technicalSpecifications: slimSource(product?.technicalSpecifications),
        technicalDetails: slimSource(product?.technicalDetails),
        details: slimSource(product?.details),
        itemDetails: slimSource(product?.itemDetails),
        specs: slimSource(product?.specs),
        productDetails: slimSource(product?.productDetails)
      });
    }

    const items = [...unique.values()];

    if (!items.length) {
      return { found: new Map(), consulted: 0 };
    }

    const mlToken = this.getAccessToken();

    if (!mlToken) {
      throw new Error('Falta la conexión de Mercado Libre.');
    }

    const batches = [];

    for (let n = 0; n < items.length; n += 5) {
      batches.push(items.slice(n, n + 5));
    }

    const found = new Map();
    let cursor = 0;
    let processed = 0;

    const worker = async () => {
      while (cursor < batches.length) {
        const batch = batches[cursor++];

        const { data, error } = await supabase.auth.getSession();
        const sessionToken = data?.session?.access_token;

        if (error || !sessionToken) {
          throw new Error(
            'Tu sesión de Mercado Bulk expiró. Inicia sesión nuevamente.'
          );
        }

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 120000);

        let response;

        try {
          response = await fetch(
            `${this.apiBaseUrl}/api/mercadolibre/catalog-attributes/collect`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${sessionToken}`
              },
              body: JSON.stringify({
                products: batch,
                accessToken: mlToken
              }),
              signal: controller.signal
            }
          );
        } finally {
          clearTimeout(timer);
        }

        const payload = await response.json().catch(() => ({}));

        if (
          !response.ok ||
          payload?.success !== true ||
          !Array.isArray(payload.data)
        ) {
          throw new Error(
            `Error recuperando atributos HTTP ${response.status}: ` +
            (payload?.message || 'Respuesta inválida')
          );
        }

        if (payload.data.length !== batch.length) {
          throw new Error(
            'Mercado Bulk no devolvió todos los resultados del lote.'
          );
        }

        for (const result of payload.data) {
          if (result?.status === 'error') {
            throw new Error(
              `Error consultando catálogo para ${result?.asin || 'ASIN desconocido'}`
            );
          }

          if (
            result?.status === 'found' &&
            Array.isArray(result.attributes)
          ) {
            found.set(
              `${String(result.asin).toUpperCase()}|${result.category_id}`,
              result.attributes
            );
          }
        }

        processed += batch.length;

        if (processed % 50 < batch.length ||
            processed === items.length) {
          console.info(
            `[Atributos MCO] ${processed}/${items.length} consultados`
          );
        }
      }
    };

    await Promise.all(
      Array.from(
        { length: Math.min(2, batches.length) },
        () => worker()
      )
    );

    return { found, consulted: items.length };
  }

  async previewPreparedAttributes(products) {
    const items = Array.isArray(products) ? products.filter(item => item && typeof item === 'object') : [];
    if (!items.length) {
      return { success: true, previews: [] };
    }

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return { success: false, error: 'Inicia sesión con MercadoLibre para consultar los atributos de la categoría.' };
    }

    try {
      const resolvedItems = await Promise.all(items.map(async (product) => {
        try {
          const validation = await this.validateCategoryAndShipping(product);
          const resolvedProduct = validation?.correctedProduct || product;
          const effectiveCategoryId = resolvedProduct?.category_id || product?.category_id || product?.categoria || product?.category || '';
          const categoryIssues = (validation?.errors || []).some(message => /categor[ií]a/i.test(String(message)));
          const categoryUnconfirmed = (validation?.warnings || []).some(message => /no fue posible confirmar temporalmente la categor[ií]a/i.test(String(message)));
          return {
            product: resolvedProduct,
            categoryResolution: {
              categoryId: effectiveCategoryId,
              provisional: !effectiveCategoryId || categoryIssues || categoryUnconfirmed,
              warnings: validation?.warnings || [],
              errors: validation?.errors || []
            }
          };
        } catch (error) {
          return {
            product,
            categoryResolution: {
              categoryId: product?.category_id || product?.categoria || product?.category || '',
              provisional: true,
              warnings: [],
              errors: [error?.message || 'No se pudo resolver la categoría antes de preparar atributos.']
            }
          };
        }
      }));
      const previewItems = resolvedItems.map((item, index) => ({
        ...item,
        index,
        product: compactProductForAttributePreview(item.product)
      }));
      const previewBatches = splitAttributePreviewRequests(previewItems, accessToken);
      const previews = new Array(previewItems.length);
      const requestMetrics = { initialBatches: previewBatches.length, sent: [], subdivisions: 0 };

      const processBatch = async (batch, splitDepth = 0) => {
        if (batch.oversized) {
          const item = batch.items[0];
          previews[item.index] = createPreviewError(
            item,
            'preview_product_too_large',
            'El producto supera el presupuesto seguro de la previsualización después de excluir datos no usados.',
            batch.bytes,
            { largestFields: getPreviewFieldSizes(item.product) }
          );
          return;
        }

        const response = await fetch(`${this.apiBaseUrl}/api/mercadolibre/publish-product`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: batch.body
        });
        const result = await response.json().catch(() => ({}));
        requestMetrics.sent.push({ bytes: batch.bytes, products: batch.items.length, status: response.status, splitDepth });

        if (response.status === 413) {
          if (batch.items.length === 1) {
            const item = batch.items[0];
            previews[item.index] = createPreviewError(
              item,
              'preview_request_entity_too_large',
              'El servidor rechazó la previsualización individual por tamaño.',
              batch.bytes,
              { largestFields: getPreviewFieldSizes(item.product) }
            );
            return;
          }
          requestMetrics.subdivisions += 1;
          const [leftBatch, rightBatch] = splitPreviewBatch(batch, accessToken);
          await processBatch(leftBatch, splitDepth + 1);
          await processBatch(rightBatch, splitDepth + 1);
          return;
        }

        if (!response.ok || !result?.success) {
          throw new Error(result?.message || `HTTP ${response.status}`);
        }
        const batchPreviews = Array.isArray(result.data) ? result.data : [];
        batch.items.forEach((previewItem, batchIndex) => {
          const preview = batchPreviews[batchIndex];
          previews[previewItem.index] = preview
            ? { ...preview, categoryResolution: previewItem.categoryResolution }
            : createPreviewError(
              previewItem,
              'preview_response_missing_product',
              'El servidor no devolvió un resultado para este producto.',
              batch.bytes
            );
        });
      };

      for (const batch of previewBatches) {
        await processBatch(batch);
      }
      return {
        success: true,
        previews: previewItems.map((previewItem) => previews[previewItem.index] || createPreviewError(
          previewItem,
          'preview_result_missing',
          'La previsualización no produjo un resultado para este producto.',
          0
        )),
        requestMetrics
      };
    } catch (error) {
      return { success: false, error: error?.message || 'No se pudo preparar los atributos de la categoría.' };
    }
  }

  // Special validation for problematic products
  validateProblematicProduct(product) {
    const title = product?.titulo || product?.title || '';
    
    // Check for specific problematic products
    const problematicProducts = [
      'Recorte De Poda De Micro Punta De Antiadherente',
      'Adaptador Compatible 12v'
    ];
    
    if (problematicProducts.some(p => title.toLowerCase().includes(p.toLowerCase()))) {
      console.warn(`⚠️ Detected problematic product: "${title}"`);
      console.log('🔍 Raw product data:', JSON.stringify(product, null, 2));
      
      // Check specific fields that might be problematic
      const issues = [];
      
      if (!product?.precio && !product?.price) {
        issues.push('Missing price field');
      }
      
      if (!product?.stock && !product?.quantity && !product?.available_quantity) {
        issues.push('Missing stock/quantity field');
      }
      
      if (!product?.categoria && !product?.category) {
        issues.push('Missing category field');
      }
      
      if (issues.length > 0) {
        console.error(`❌ Issues found in problematic product "${title}":`, issues);
      }
    }
  }

  // Transform product data to MercadoLibre format without local validation
  async transformProductToMLFormat(product) {
    console.log(`🔄 Preparando producto para publicación: "${product?.titulo || product?.title || 'sin título'}"`);

    const baseProduct = Array.isArray(product) ? {} : { ...product };
    const mlProduct = { ...baseProduct };

    if (!mlProduct.title) {
      mlProduct.title = baseProduct.titulo ?? baseProduct.title ?? '';
    }

    const titleForDetection = mlProduct.title || baseProduct.titulo || baseProduct.title || '';
    const initialCategoryId = mlProduct.category_id
      ?? baseProduct.category_id
      ?? baseProduct.categoria
      ?? baseProduct.category
      ?? '';

    const categoryResolvedByPreflight = isPreflightCategoryResolved(product);
    if (categoryResolvedByPreflight) {
      mlProduct.category_id = initialCategoryId;
    } else {
      const originalCategoryDetail = /^MCO\d+$/i.test(String(initialCategoryId))
        ? await this.getCategoryDetail(initialCategoryId)
        : null;
      const originalCategoryIsPublishable = Boolean(
        originalCategoryDetail &&
        Array.isArray(originalCategoryDetail.children_categories) &&
        originalCategoryDetail.children_categories.length === 0 &&
        originalCategoryDetail.settings?.listing_allowed !== false
      );
      const categoryLookupUnavailable = Boolean(initialCategoryId && !originalCategoryDetail);
      if (originalCategoryIsPublishable || categoryLookupUnavailable) {
        mlProduct.category_id = initialCategoryId;
      } else {
        mlProduct.category_id = await this.resolveCategoryId(initialCategoryId, titleForDetection) || '';
      }
      if (!mlProduct.category_id && this.defaultCategoryId) {
        mlProduct.category_id = this.defaultCategoryId;
      }

      // Validar nuevamente que la categoría final sea hoja
      if (mlProduct.category_id && !categoryLookupUnavailable) {
        const isLeafFinal = await this.isLeafCategory(mlProduct.category_id);
        if (!isLeafFinal) {
          const fallbackCategory = await this.resolveCategoryId('', titleForDetection);
          if (fallbackCategory) {
            mlProduct.category_id = fallbackCategory;
          }
        }
      }
    }

    delete mlProduct.category;
    delete mlProduct.categoria;

    if (mlProduct.price === undefined) {
      mlProduct.price = baseProduct.precio ?? baseProduct.price;
    }
    if (mlProduct.currency_id === undefined) {
      mlProduct.currency_id = baseProduct.currency_id ?? baseProduct.moneda ?? 'COP';
    }

    if (mlProduct.available_quantity === undefined) {
      if (baseProduct.available_quantity !== undefined) {
        mlProduct.available_quantity = baseProduct.available_quantity;
      } else if (baseProduct.stock !== undefined) {
        mlProduct.available_quantity = baseProduct.stock;
      } else if (baseProduct.quantity !== undefined) {
        mlProduct.available_quantity = baseProduct.quantity;
      } else {
        mlProduct.available_quantity = 0;
      }
    }

    const parsedQuantity = Number(mlProduct.available_quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      mlProduct.available_quantity = 3;
    } else {
      // Forzar stock publicado en 3 según requerimiento
      mlProduct.available_quantity = 3;
    }

    if (!mlProduct.condition) {
      mlProduct.condition = baseProduct.condition ?? baseProduct.condicion ?? 'new';
    }
    if (!mlProduct.listing_type_id) {
      mlProduct.listing_type_id = baseProduct.listing_type_id ?? baseProduct.tipoPublicacion ?? 'gold_special';
    }
    if (!mlProduct.buying_mode) {
      mlProduct.buying_mode = baseProduct.buying_mode ?? 'buy_it_now';
    }

    const pictureSources = new Set();
    const addPictureSource = (value) => {
      if (!value || typeof value !== 'string') return;
      const trimmed = value.trim();
      if (!trimmed) return;
      pictureSources.add(trimmed.replace(/^"|"$/g, ''));
    };

    function extractFromString(raw) {
      if (typeof raw !== 'string') return;
      const trimmed = raw.trim();
      if (!trimmed) return;
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parsed.forEach((item) => processImageValue(item));
            return;
          }
        } catch {}
      }
      trimmed
        .split(import.meta.env?.VITE_MELI_PICTURE_REPAIR_ENABLED === 'true'
          ? /[;\n\r\t\|]+|,(?=\s*https?:\/\/)/i
          : /[;\n\r\t\|,]+/)
        .map(part => part.trim())
        .filter(Boolean)
        .forEach(addPictureSource);
    }

    function processImageValue(value) {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach((item) => processImageValue(item));
        return;
      }
      if (typeof value === 'string') {
        extractFromString(value);
        return;
      }
      if (typeof value === 'object') {
        const explicitSource = value.source || value.url || value.secure_url || value.path;
        if (explicitSource) {
          addPictureSource(explicitSource);
        }
        const nestedCandidates = ['sources', 'urls', 'images', 'pictures'];
        nestedCandidates.forEach((key) => {
          if (value[key] && value[key] !== value) {
            processImageValue(value[key]);
          }
        });
      }
    }

    processImageValue(baseProduct.highResolutionImages);
    processImageValue(baseProduct.high_resolution_images);
    processImageValue(baseProduct['Imágenes de alta resolución']);
    processImageValue(baseProduct['Imagenes de alta resolucion']);
    processImageValue(mlProduct.pictures);
    processImageValue(baseProduct.pictures);
    processImageValue(baseProduct.images);
    processImageValue(baseProduct.imagenes);
    processImageValue(baseProduct.image_urls);
    const imageFields = ['imagen1', 'imagen2', 'imagen3', 'imagen4', 'imagen5', 'image1', 'image2', 'image3', 'image4', 'image5'];
    imageFields.forEach((field) => processImageValue(baseProduct?.[field]));

    Object.keys(baseProduct || {}).forEach((key) => {
      const lower = key.toLowerCase();
      const normalizedKey = lower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalizedKey.includes('miniatura') || normalizedKey.includes('thumbnail')) {
        return;
      }
      if (normalizedKey.includes('imagen') || normalizedKey.includes('image_url') || normalizedKey.includes('picture')) {
        if (!imageFields.includes(key)) {
          processImageValue(baseProduct[key]);
        }
      }
    });

    const normalizedPictures = Array.from(pictureSources)
      .filter(Boolean)
      .slice(0, import.meta.env?.VITE_MELI_PICTURE_REPAIR_ENABLED === 'true' ? Infinity : 10)
      .map((src) => ({ source: src }));

    if (normalizedPictures.length > 0) {
      mlProduct.pictures = normalizedPictures;
    } else {
      delete mlProduct.pictures;
      console.warn(`⚠️ Producto sin imágenes válidas: ${mlProduct?.title || titleForDetection}`);
    }

    if (!mlProduct.description) {
      const descriptionText = baseProduct.descripcion ?? baseProduct.description;
      if (typeof descriptionText === 'string' && descriptionText.trim()) {
        mlProduct.description = { plain_text: descriptionText };
      }
    } else if (typeof mlProduct.description === 'string') {
      mlProduct.description = { plain_text: mlProduct.description };
    }

    if (!mlProduct.attributes && baseProduct.attributes) {
      mlProduct.attributes = baseProduct.attributes;
    }

    if (!mlProduct.sale_terms && baseProduct.garantia) {
      mlProduct.sale_terms = [
        {
          id: 'WARRANTY_TYPE',
          value_name: String(baseProduct.garantia)
        }
      ];
    }

    delete mlProduct.manufacturingTimeDays;

    const existingShipping = mlProduct.shipping && typeof mlProduct.shipping === 'object' && !Array.isArray(mlProduct.shipping)
      ? mlProduct.shipping
      : {};
    const hasFreeShipping = mlProduct.free_shipping === true || existingShipping.free_shipping === true;
    mlProduct.shipping = {
      ...existingShipping,
      mode: existingShipping?.mode || 'not_specified',
      ...(hasFreeShipping ? { free_shipping: true } : {})
    };
    delete mlProduct.shipping_mode;
    delete mlProduct.modo_envio;
    delete mlProduct.shipping_modes;

    return mlProduct;
  }

  async isLeafCategory(categoryId) {
    if (!categoryId) return false;
    const normalized = String(categoryId).trim();
    if (!normalized) return false;

    if (this.knownNonLeafCategories.has(normalized)) {
      return false;
    }

    if (this.categoryLeafStatus.has(normalized)) {
      return this.categoryLeafStatus.get(normalized);
    }

    const apiBaseUrl = this.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const endpoints = [];
    if (apiBaseUrl) {
      endpoints.push({
        url: `${apiBaseUrl}/api/mercadolibre/category/${normalized}`,
        options: { headers: { 'Authorization': `Bearer ${this.getAccessToken()}` } }
      });
    }
    endpoints.push({ url: `https://api.mercadolibre.com/categories/${normalized}`, options: {} });

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint.url, endpoint.options);
        if (!response?.ok) {
          continue;
        }
        const payload = await response.json().catch(() => ({}));
        const detail = payload?.data || payload || {};
        const children = Array.isArray(detail.children_categories) ? detail.children_categories : [];
        const isLeaf = children.length === 0;
        this.categoryLeafStatus.set(normalized, isLeaf);
        if (!isLeaf) {
          this.knownNonLeafCategories.add(normalized);
        }
        return isLeaf;
      } catch (error) {
        console.warn('No se pudo verificar si la categoría es hoja:', normalized, error?.message || error);
      }
    }

    return true;
  }

  async getCategoryDetail(categoryId) {
    if (!categoryId) return null;
    const normalized = String(categoryId).trim();
    if (!normalized) return null;

    if (this.categoryDetailCache.has(normalized)) {
      return this.categoryDetailCache.get(normalized);
    }

    const apiBaseUrl = this.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
    const endpoints = [];
    if (apiBaseUrl) endpoints.push({
      url: `${apiBaseUrl}/api/mercadolibre/category/${normalized}`,
      options: { headers: { 'Authorization': `Bearer ${this.getAccessToken()}` } }
    });
    endpoints.push({ url: `https://api.mercadolibre.com/categories/${normalized}`, options: {} });

    for (const endpoint of endpoints) {
      try {
        const res = await fetch(endpoint.url, endpoint.options);
        if (!res?.ok) continue;
        const payload = await res.json().catch(() => ({}));
        const detail = payload?.data || payload || null;
        if (detail) {
          this.categoryDetailCache.set(normalized, detail);
          if (Array.isArray(detail.children_categories)) {
            this.categoryLeafStatus.set(normalized, detail.children_categories.length === 0);
            if (detail.children_categories.length > 0) {
              this.knownNonLeafCategories.add(normalized);
            }
          }
          return detail;
        }
      } catch (error) {
        console.warn('No se pudo obtener detalle de categoría:', normalized, error?.message || error);
      }
    }

    return null;
  }

  async getUserShippingCapabilities(forceRefresh = false) {
    if (!forceRefresh && this.userShippingCache && this.userShippingCache.expires > Date.now()) {
      return this.userShippingCache.payload;
    }

    try {
      const info = await this.getUserInfo();
      if (!info?.success || !info?.user) {
        return null;
      }

      const user = info.user;
      const shippingModes = new Set();
      if (Array.isArray(user?.shipping_modes)) {
        user.shipping_modes.forEach(mode => shippingModes.add(mode));
      } else if (typeof user?.shipping_modes === 'string') {
        shippingModes.add(user.shipping_modes);
      }

      const logisticTypes = new Set();
      const logisticObject = user?.seller_reputation?.shipping || user?.shipping || {};
      if (Array.isArray(logisticObject?.logistic_types)) {
        logisticObject.logistic_types.forEach(mode => logisticTypes.add(mode));
      } else if (typeof logisticObject?.logistic_type === 'string') {
        logisticTypes.add(logisticObject.logistic_type);
      } else if (typeof logisticObject?.mode === 'string') {
        logisticTypes.add(logisticObject.mode);
      }

      const payload = {
        modes: shippingModes,
        logisticTypes
      };

      this.userShippingCache = {
        payload,
        expires: Date.now() + (10 * 60 * 1000) // 10 minutos
      };

      return payload;
    } catch (error) {
      console.warn('No se pudieron obtener capacidades de envío del usuario:', error?.message || error);
      return null;
    }
  }

  async resolveCategoryId(currentCategoryId, titleForDetection) {
    const normalizedCurrent = typeof currentCategoryId === 'string' ? currentCategoryId.trim() : (currentCategoryId || '');

    if (normalizedCurrent && normalizedCurrent !== 'MCO1000') {
      const resolvedBase = await resolvePreflightCategory({
        candidates: [],
        requestedCategoryId: normalizedCurrent,
        getCategoryDetail: (categoryId) => this.getCategoryDetail(categoryId)
      });
      if (resolvedBase.effectiveCategoryId) {
        return resolvedBase.effectiveCategoryId;
      }
    }

    if (titleForDetection) {
      const candidates = await this.detectCategoryCandidates(titleForDetection);
      const resolvedPrediction = await resolvePreflightCategory({
        candidates,
        requestedCategoryId: '',
        getCategoryDetail: (categoryId) => this.getCategoryDetail(categoryId)
      });
      if (resolvedPrediction.effectiveCategoryId) {
        return resolvedPrediction.effectiveCategoryId;
      }
    }
    return null;
  }

  // Buscar recursivamente la primera subcategoría hoja publicable dentro del subárbol de una categoría
  async findFirstValidLeafCategory(categoryId, depth = 0, maxDepth = 10) {
    if (!categoryId || depth > maxDepth) return null;
    try {
      const detail = await this.getCategoryDetail(categoryId);
      if (!detail) return null;
      const settings = detail?.settings || {};
      const children = Array.isArray(detail?.children_categories) ? detail.children_categories : [];
      const listingAllowed = settings?.listing_allowed !== false;

      // Si no tiene hijos y permite publicar, es una hoja válida
      if (!children.length && listingAllowed) {
        return detail.id || categoryId;
      }

      // Si tiene hijos, recorrer recursivamente hasta encontrar la primera hoja válida
      for (const child of children) {
        const childId = typeof child === 'object' ? child.id : child;
        if (!childId) continue;
        const resolved = await this.findFirstValidLeafCategory(childId, depth + 1, maxDepth);
        if (resolved) return resolved;
      }
    } catch (e) {
      console.warn('Error buscando hoja válida en subárbol de categoría:', categoryId, e?.message || e);
    }
    return null;
  }

  async detectCategoryId(title) {
    const candidates = await this.detectCategoryCandidates(title);
    return candidates[0]?.category_id || null;
  }

  async detectCategoryCandidates(title) {
    try {
      if (!title || typeof title !== 'string') return [];
      const normalizedTitle = title.trim().toLowerCase();
      if (!normalizedTitle) return [];

      if (this.categoryDiscoveryCache.has(normalizedTitle)) {
        return this.categoryDiscoveryCache.get(normalizedTitle);
      }

      const apiBaseUrl = this.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
      if (!apiBaseUrl) return [];
      const accessToken = this.getAccessToken();

      const response = await fetch(`${apiBaseUrl}/api/mercadolibre/category-detect?q=${encodeURIComponent(title)}`, {
        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}
      });
      if (!response.ok) {
        console.warn('No se pudo detectar categoría automáticamente:', response.status, await response.text().catch(() => ''));
        return [];
      }

      const payload = await response.json().catch(() => ({}));
      const candidates = Array.isArray(payload?.data?.candidates) ? payload.data.candidates.slice(0, 3) : [];
      this.categoryDiscoveryCache.set(normalizedTitle, candidates);
      return candidates;
    } catch (error) {
      console.warn('Error detectando categoría automática:', error);
      return [];
    }
  }

  // Extract brand from title
  extractBrand(title) {
    if (!title || typeof title !== 'string') {
      return 'Genérico';
    }

    const brands = [
      // Tecnología
      'Apple', 'Samsung', 'Sony', 'LG', 'Nintendo', 'Microsoft', 'HP', 'Dell', 'Lenovo', 'ASUS',
      'Xiaomi', 'Huawei', 'OnePlus', 'Google', 'Amazon', 'Logitech', 'Canon', 'Nikon', 'Epson',
      'Acer', 'MSI', 'Razer', 'Corsair', 'Intel', 'AMD', 'NVIDIA', 'Philips', 'Panasonic',
      
      // Hogar y muebles  
      'IKEA', 'Black+Decker', 'Stanley', 'Makita', 'DeWalt', 'Bosch', 'Karcher', 'Whirlpool',
      'Electrolux', 'Frigidaire', 'KitchenAid', 'Cuisinart', 'Hamilton Beach', 'Oster',
      
      // Moda y accesorios
      'Nike', 'Adidas', 'Puma', 'Reebok', 'Under Armour', 'New Balance', 'Converse', 'Vans',
      'Levi\'s', 'Calvin Klein', 'Tommy Hilfiger', 'Guess', 'Polo Ralph Lauren',
      
      // Genéricos comunes
      'Generic', 'Unbranded', 'No Brand'
    ];
    
    const titleLower = title.toLowerCase();
    const foundBrand = brands.find(brand => 
      titleLower.includes(brand.toLowerCase())
    );
    
    // Si encontramos una marca genérica, retornar como "Genérico"
    if (foundBrand && ['Generic', 'Unbranded', 'No Brand'].includes(foundBrand)) {
      return 'Genérico';
    }
    
    return foundBrand || 'Genérico';
  }

  // VALIDACIÓN AVANZADA: Verificar categoría leaf y modo de envío
  async validateCategoryAndShipping(product) {
    const errors = [];
    const warnings = [];
    let correctedProduct = { ...product };

    const title = correctedProduct?.titulo || correctedProduct?.title || '';
    const requestedCategory = correctedProduct?.category_id
      || correctedProduct?.categoria
      || correctedProduct?.category
      || '';

    // Respetar la categoría del lote si Mercado Libre confirma que es hoja
    // publicable. El predictor no debe reclasificar artículos ya categorizados.
    const requestedId = String(requestedCategory || '').trim();
    const suppliedDetail = /^MCO\d+$/.test(requestedId)
      ? await this.getCategoryDetail(requestedId)
      : null;
    if (requestedId && !suppliedDetail) {
      warnings.push(`MercadoLibre: No fue posible confirmar temporalmente la categoría original ${requestedId}. Se conserva sin reclasificar.`);
      return { isValid: true, errors, warnings, correctedProduct };
    }
    const originalIsPublishable = Boolean(
      suppliedDetail &&
      String(suppliedDetail.id || requestedId) === requestedId &&
      Array.isArray(suppliedDetail.children_categories) &&
      suppliedDetail.children_categories.length === 0 &&
      suppliedDetail.settings?.listing_allowed !== false
    );

    let candidates = [];
    let categoryResolution;
    if (originalIsPublishable) {
      categoryResolution = {
        effectiveCategoryId: requestedId,
        predictions: [],
        source: 'original_verified_leaf'
      };
    } else {
      try {
        candidates = await this.detectCategoryCandidates(title);
      } catch (error) {
        console.warn('[Category Preflight] Predictor unavailable:', error?.message || error);
      }
      categoryResolution = await resolvePreflightCategory({
        candidates,
        requestedCategoryId: requestedCategory,
        getCategoryDetail: (categoryId) => this.getCategoryDetail(categoryId)
      });
    }
    const resolvedCategoryId = categoryResolution.effectiveCategoryId;

    console.info('[Category Preflight]', {
      base: requestedCategory || 'unavailable',
      predictions: categoryResolution.predictions,
      effective: resolvedCategoryId || 'unavailable',
      source: categoryResolution.source,
      changed: Boolean(resolvedCategoryId && resolvedCategoryId !== requestedCategory)
    });

    if (resolvedCategoryId && resolvedCategoryId !== correctedProduct?.category_id) {
      correctedProduct = markPreflightCategory(correctedProduct, resolvedCategoryId);
      warnings.push(`Categoría ajustada automáticamente a ${resolvedCategoryId} para cumplir con las reglas de MercadoLibre.`);
    } else if (resolvedCategoryId) {
      correctedProduct = markPreflightCategory(correctedProduct, resolvedCategoryId);
    }

    if (!resolvedCategoryId) {
      errors.push('MercadoLibre: No fue posible determinar una categoría hoja segura para el producto.');
      return { isValid: false, errors, warnings, correctedProduct };
    }

    const isLeaf = await this.isLeafCategory(resolvedCategoryId);
    const categoryDetail = await this.getCategoryDetail(resolvedCategoryId);
    if (!isLeaf) {
      const categoryName = categoryDetail?.name || resolvedCategoryId;
      const childNames = Array.isArray(categoryDetail?.children_categories)
        ? categoryDetail.children_categories.map(child => child?.name || child?.id).filter(Boolean).slice(0, 5)
        : [];
      const suggestion = childNames.length ? ` Sugeridas: ${childNames.join(', ')}...` : '';
      errors.push(`MercadoLibre: La categoría "${categoryName}" no permite publicar. Debes elegir una subcategoría hoja.${suggestion}`);
    }

    const settings = categoryDetail?.settings || {};
    const categoryShippingModes = Array.isArray(settings?.shipping_modes) ? settings.shipping_modes : [];
    const requiresMe2 = categoryShippingModes.includes('me2') || settings?.simple_shipping === 'required';
    const requiresMe1 = categoryShippingModes.includes('me1');

    const userShipping = await this.getUserShippingCapabilities();
    const missingModes = [];
    if (requiresMe2 && (!userShipping || !userShipping.modes.has('me2'))) {
      missingModes.push('Mercado Envíos (me2)');
    }
    if (requiresMe1 && (!userShipping || !userShipping.modes.has('me1'))) {
      missingModes.push('Mercado Envíos Full (me1)');
    }
    if (missingModes.length) {
      errors.push(`MercadoLibre: Esta categoría exige ${missingModes.join(' y ')} pero tu cuenta no lo tiene habilitado. Abre ticket con soporte de MercadoLibre para activarlo o elige otra categoría.`);
    }

    if (settings?.listing_allowed === false) {
      errors.push('MercadoLibre: Esta categoría está deshabilitada para publicaciones nuevas. Debes seleccionar otra.');
    }

    if (!correctedProduct?.shipping) {
      correctedProduct.shipping = { mode: 'not_specified' };
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      correctedProduct
    };
  }

  // REAL API CALL - Publish single product to MercadoLibre
  async publishProduct(product) {
    try {
      const manufacturingTimeDays = product?.manufacturingTimeDays;
      // Validar autenticación y refrescar token si es necesario
      const currentAccessToken = this.getAccessToken?.();

      if (!currentAccessToken) {
        throw new Error('No se encontró token de Mercado Libre para el seller activo. Vuelve a conectar esta cuenta desde Configuración de API.');
      }

      // NUEVA VALIDACIÓN: Verificar categoría leaf y modo de envío ANTES de transformar
      console.log(`🔍 Iniciando validación avanzada para: ${product?.titulo || product?.title}`);
      const validation = await this.validateCategoryAndShipping(product);
      
      if (!validation.isValid) {
        console.error(`❌ Producto no válido:`, validation.errors);
        return {
          success: false,
          productId: product?.id,
          productTitle: product?.titulo || product?.title,
          error: {
            productId: product?.id,
            productTitle: product?.titulo || product?.title,
            message: validation.errors.join(', '),
            type: 'validation_error',
            timestamp: new Date()?.toISOString(),
            retryable: false
          }
        };
      }

      // Usar producto corregido si hay correcciones
      const correctedProduct = validation.correctedProduct;
      
      // Log de correcciones aplicadas
      if (validation.warnings.length > 0) {
        console.log(`⚠️ Correcciones aplicadas:`, validation.warnings);
      }

      // Calcular precio rentable real para productos provenientes de Amazon.
      // El backend usa TRM + transporte + costos reales de Mercado Libre
      // + rentabilidad mínima + redondeo final.
      const originalPublishPrice = Number(correctedProduct?.precio ?? product?.precio ?? correctedProduct?.price ?? product?.price);
      const amazonPriceUsd = Number(
        correctedProduct?.amazonPriceUsd ??
        correctedProduct?.amazon_price_usd
      );

      if (Number.isFinite(amazonPriceUsd) && amazonPriceUsd > 0) {
        const pricingResponse = await fetch(
          `${this.apiBaseUrl}/api/pricing/calculate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.getAccessToken()}`
            },
            body: JSON.stringify({
              product: correctedProduct
            })
          }
        );

        let pricingResult = null;

        try {
          pricingResult = await pricingResponse.json();
        } catch {
          pricingResult = null;
        }

        const profitablePrice = Number(pricingResult?.finalPriceCop);

        if (
          !pricingResponse.ok ||
          pricingResult?.success === false ||
          !Number.isFinite(profitablePrice) ||
          profitablePrice <= 0
        ) {
          const pricingMessage =
            pricingResult?.message ||
            pricingResult?.error ||
            `HTTP ${pricingResponse.status}`;

          throw new Error(
            `No se pudo calcular el precio rentable: ${pricingMessage}`
          );
        }

        correctedProduct.price = Math.round(profitablePrice);
        correctedProduct.precio = correctedProduct.price;

        const currentShipping =
          correctedProduct?.shipping &&
          typeof correctedProduct.shipping === 'object' &&
          !Array.isArray(correctedProduct.shipping)
            ? correctedProduct.shipping
            : {};

        correctedProduct.shipping = {
          ...currentShipping,
          mode:
            pricingResult?.shippingMode ||
            currentShipping?.mode ||
            'not_specified',
          free_shipping: pricingResult?.freeShipping === true,
          ...(pricingResult?.logisticType
            ? { logistic_type: pricingResult.logisticType }
            : {})
        };

        correctedProduct.free_shipping =
          pricingResult?.freeShipping === true;

        if (Number.isFinite(originalPublishPrice) && originalPublishPrice > 0) {
          correctedProduct.price = originalPublishPrice;
          correctedProduct.precio = originalPublishPrice;
        }

        console.log('💰 Precio rentable calculado:', {
          amazonPriceUsd,
          finalPriceCop: correctedProduct.price,
          profitPercent: pricingResult?.profitPercent,
          mercadoLibreFeeCop: pricingResult?.mercadoLibreFeeCop,
          mercadoLibreShippingCostCop:
            pricingResult?.mercadoLibreShippingCostCop
        });
      }

      // Preparar SIEMPRE una descripción final (plantilla + cuerpo) para que no quede vacía.
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      const cleanPlainText = (input) => {
        if (input === null || input === undefined) return '';
        let text = String(input);

        // Normalizar saltos de línea
        text = text.replace(/\r\n?/g, '\n');

        // Remover caracteres de control (excepto \n y \t)
        text = text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');

        // Si viene con HTML, intentar pasarlo a texto
        const looksLikeHtml = /<\s*\/?\s*[a-z][\s\S]*>/i.test(text);
        if (looksLikeHtml) {
          text = text
            .replace(/<\s*script\b[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, '')
            .replace(/<\s*style\b[^>]*>[\s\S]*?<\s*\/\s*style\s*>/gi, '')
            .replace(/<\s*br\s*\/?>/gi, '\n')
            .replace(/<\s*\/\s*p\s*>/gi, '\n')
            .replace(/<\s*\/\s*li\s*>/gi, '\n')
            .replace(/<\s*\/\s*div\s*>/gi, '\n')
            .replace(/<[^>]+>/g, '');
        }

        // Compactar espacios preservando saltos de línea
        text = text
          .split('\n')
          .map(line => line.replace(/[ \t\f\v]+/g, ' ').trimEnd())
          .join('\n')
          .replace(/\n{4,}/g, '\n\n\n');

        return text.trim();
      };

      const resolveBodyFromInput = (src) => {
        if (!src || typeof src !== 'object') return '';
        if (typeof src.description === 'object' && typeof src.description?.plain_text === 'string') return src.description.plain_text;
        if (typeof src.description === 'string') return src.description;
        if (typeof src.descripcion === 'string') return src.descripcion;
        return '';
      };

      const genericBody = [
        'Publicación informativa correspondiente a un artículo de uso general. La información mostrada en esta publicación se limita a los datos disponibles del producto al momento de su carga.',
        '',
        'Las características, diseño, presentación y funcionalidad pueden variar según el modelo o versión del artículo. Esta descripción no pretende sustituir información técnica específica ni detallar propiedades particulares del producto.',
        '',
        'El artículo se ofrece tal como se describe en el título de la publicación, sin incluir información adicional no especificada expresamente.'
      ].join('\n');

      const srcBodyRaw = (resolveBodyFromInput(product) || resolveBodyFromInput(correctedProduct) || '').trim();
      const srcBodyClean = cleanPlainText(srcBodyRaw);
      const bodyForTemplate = (srcBodyClean && srcBodyClean.length >= 40) ? srcBodyClean : genericBody;

      const storeName = (() => {
        try {
          if (typeof window !== 'undefined') {
            const sellerId = window.localStorage?.getItem('mercadolibre_user_id') || ''; const saved = sellerId ? (window.localStorage?.getItem('ml_store_name:' + sellerId) || '') : '';
            if (saved && saved.trim()) return saved.trim();
          }
        } catch {}
        const envName = import.meta.env?.VITE_STORE_NAME || '';
        return String(envName || '').trim();
      })();

      const headerLine = storeName ? `BIENVENIDOS SOMOS ${storeName} !` : 'BIENVENIDOS SOMOS!';
      const rememberLine = storeName ? `RECUERDA SOMOS ${storeName}` : 'RECUERDA';
      const template = [
        headerLine,
        'LIDERES EN MERCADOLIBRE EN PRODUCTOS IMPORTADOS CON 100% DE CALIFICACIONES POSITIVAS!',
        '',
        '---------------------------------------------------------------------------------------------------------------',
        'TIEMPOS DE ENTREGA DE 6 A 10 DIAS HABILES / ENVIO GRATIS',
        '---------------------------------------------------------------------------------------------------------------',
        '',
        'DESCRIPCION DEL PRODUCTO OFERTADO :',
        '',
        (typeof bodyForTemplate === 'string' ? bodyForTemplate : ''),
        '',
        '---------------------------------------------------------------------------------------------------------------',
        '',
        rememberLine,
        'ESTAMOS ENTRE LOS VENDEDORES MAS CONFIABLES DE MERCADOLIBRE !',
        '',
        'Sin garantía'
      ].join('\n');

      const finalDescriptionText = cleanPlainText(template).slice(0, 50000);

      // Transform product to MercadoLibre format usando el producto corregido
      const mlProduct = await this.transformProductToMLFormat(correctedProduct);
      mlProduct.description = { plain_text: finalDescriptionText };

      console.log(`🚀 Publicando producto real en MercadoLibre: ${mlProduct?.title}`);
      console.log('📊 Datos del producto completos:', JSON.stringify(mlProduct, null, 2));
      
      // Make API call through our backend proxy to avoid CORS issues
      const apiBaseUrl = this.apiBaseUrl;
      const response = await postPublishProductRequest({
        apiBaseUrl,
        product: mlProduct,
        accessToken: currentAccessToken,
        manufacturingTimeDays
      });

      console.log(`🔍 Respuesta de la API - Status: ${response.status}`);
      
      if (!response.ok) {
        let errorData;
        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          errorData = await response.json();
        } catch (parseError) {
          const textResponse = await response.text();
          errorData = textResponse ? { message: textResponse } : {};
        }

        const mlPayload = errorData?.error && typeof errorData.error === 'object' ? errorData.error : errorData;
        let mlMessage = fallbackMessage;

        if (typeof errorData?.message === 'string' && errorData.message.trim().length > 0) {
          mlMessage = errorData.message;
        } else if (typeof mlPayload?.message === 'string' && mlPayload.message.trim().length > 0) {
          mlMessage = mlPayload.message;
        }

        const collectMessages = (items = []) => items
          .map(entry => {
            if (!entry) return null;
            if (typeof entry === 'string') return entry;
            if (entry?.message) return entry.message;
            const parts = [entry?.code, entry?.field, entry?.description, entry?.value]
              .filter(Boolean)
              .map(String);
            return parts.length ? parts.join(' - ') : null;
          })
          .filter(Boolean);

        const layerMessages = collectMessages(mlPayload?.cause);
        const nestedMessages = collectMessages(mlPayload?.errors);
        const extraMessages = collectMessages(errorData?.cause).concat(collectMessages(errorData?.errors));

        const allMessages = [...layerMessages, ...nestedMessages, ...extraMessages];
        if (allMessages.length > 0) {
          mlMessage = `${mlMessage} | Detalles: ${allMessages.join(' | ')}`;
        }

        const normalized = mlMessage?.toLowerCase?.() || '';
        if (
          (!normalized || normalized === 'validation error' || normalized === fallbackMessage.toLowerCase()) &&
          mlPayload && Object.keys(mlPayload).length > 0
        ) {
          mlMessage = `${mlMessage || 'Error'} | Payload: ${JSON.stringify(mlPayload)}`;
        }

        const finalMessage = `MercadoLibre ${response.status}: ${mlMessage}`;
        const errorToThrow = new Error(finalMessage);
        errorToThrow.mlError = mlPayload;
        errorToThrow.status = response.status;
        throw errorToThrow;
      }

      const result = await response.json();
      const publishedItem = result.data;

      if (result?.description?.success === false) {
        console.error(`⚠️ Producto publicado sin descripción confirmada: ${publishedItem?.id || 'sin ID'}`, result.description);
        console.error('[Description Upload Failed]', {
          itemId: result.description?.itemId || publishedItem?.id || null,
          categoryId: result.description?.categoryId || mlProduct?.category_id || null,
          status: result.description?.status || null,
          errorCode: result.description?.errorCode || null,
          errorMessage: result.description?.errorMessage || null,
          upstreamError: result.description?.upstreamError || null
        });
      }

      console.log(`✅ Producto publicado exitosamente. ID: ${publishedItem?.id}`);
      
      return {
        success: true,
        productId: product?.id,
        productTitle: product?.titulo || product?.title,
        mercadoLibreId: publishedItem?.id,
        permalink: publishedItem?.permalink,
        status: publishedItem?.status,
        descriptionPublished: result?.description?.success !== false,
        descriptionError: result?.description?.success === false ? result.description.message : '',
        descriptionDiagnostic: result?.description || null,
        data: publishedItem
      };

    } catch (error) {
      console.error(`❌ Error publicando producto: ${product?.titulo || product?.title}`);
      console.error('🔍 Error completo:', error);
      
      // Capturar todos los posibles tipos de error
      let errorMessage = 'Error desconocido al publicar producto';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data) {
        const apiError = error.response.data;
        if (apiError?.message) {
          errorMessage = apiError.message;
        } else if (apiError?.cause && apiError.cause.length > 0) {
          errorMessage = apiError.cause.map(c => c.message || c).join(', ');
        } else if (apiError?.error) {
          errorMessage = apiError.error;
        } else {
          const apiBaseUrl = this.apiBaseUrl;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      const statusCode = error?.status || error?.response?.status || null;

      return {
        success: false,
        productId: product?.id,
        productTitle: product?.titulo || product?.title,
        error: {
          productId: product?.id,
          productTitle: product?.titulo || product?.title,
          message: errorMessage,
          timestamp: new Date()?.toISOString(),
          retryable: statusCode !== 400 && statusCode !== 403,
          raw: error?.mlError || error?.response?.data || null,
          status: statusCode
        }
      };
    }
  }

  // Update existing product
  async updateProduct(mercadoLibreId, product) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('No estás autenticado con MercadoLibre');
      }

      const mlProduct = await this.transformProductToMLFormat(product);
      
      console.log(`🔄 Actualizando producto en MercadoLibre: ${mercadoLibreId}`);
      
      const response = await this.api?.put(`/items/${mercadoLibreId}`, mlProduct);
      
      console.log(`✅ Producto actualizado exitosamente: ${mercadoLibreId}`);
        const apiBaseUrl = this.apiBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
      return {
        success: true,
        productId: product?.id,
        productTitle: product?.titulo || product?.title,
        mercadoLibreId: mercadoLibreId,
        data: response?.data
      };

    } catch (error) {
      console.error(`❌ Error actualizando producto: ${mercadoLibreId}`, error);
      
      return {
        success: false,
        productId: product?.id,
        productTitle: product?.titulo || product?.title,
        error: {
          productId: product?.id,
          productTitle: product?.titulo || product?.title,
          message: error?.response?.data?.message || error?.message || 'Error actualizando producto',
          timestamp: new Date()?.toISOString(),
          retryable: true
        }
      };
    }
  }

  // Get published items
  async getMyItems(limit = 50, offset = 0) {
    try {
      const userId = this.getUserId();
      if (!userId) throw new Error('No user ID found');

      const response = await this.api?.get(`/users/${userId}/items/search`, {
        params: { limit, offset }
      });

      return {
        success: true,
        items: response?.data?.results,
        paging: response?.data?.paging
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to get items'
      };
    }
  }

  // Pause/Resume item
  async pauseItem(itemId) {
    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('No hay token de Mercado Libre disponible para pausar el item');
      }

      const response = await this.api.put(`/items/${itemId}`, {
        status: 'paused'
      }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      return {
        success: true,
        itemId: itemId,
        status: response?.data?.status
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to pause item'
      };
    }
  }

  async resumeItem(itemId) {
    try {
      const response = await this.api?.put(`/items/${itemId}`, {
        status: 'active'
      });
      
      return {
        success: true,
        itemId: itemId,
        status: response?.data?.status
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to resume item'
      };
    }
  }

  // Delete item
  async deleteItem(itemId) {
    try {
      const response = await this.api?.put(`/items/${itemId}`, {
        status: 'closed'
      });
      
      return {
        success: true,
        itemId: itemId,
        status: response?.data?.status
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to delete item'
      };
    }
  }

  // Map descriptive category names to MercadoLibre category IDs
  mapDescriptiveCategoryToId(descriptiveCategory, validCategories) {
    if (!descriptiveCategory || typeof descriptiveCategory !== 'string') {
      return null;
    }

    const categoryMapping = {
      // Hogar y decoración
      'hogar': 'MCO1367',
      'muebles': 'MCO1071', 
      'cocina': 'MCO1367',
      'baño': 'MCO1367',
      'jardin': 'MCO1367',
      'jardín': 'MCO1367',
      'decoracion': 'MCO1367',
      'decoración': 'MCO1367',
      'textiles': 'MCO1367',
      
      // Tecnología
      'electronica': 'MCO1000',
      'electrónica': 'MCO1000', 
      'computacion': 'MCO1648',
      'computación': 'MCO1648',
      'celulares': 'MCO1051',
      'telefono': 'MCO1051',
      'teléfono': 'MCO1051',
      'camaras': 'MCO1039',
      'cámaras': 'MCO1039',
      'audio': 'MCO1000',
      'video': 'MCO1000',
      
      // Electrodomésticos
      'electrodomesticos': 'MCO1144',
      'electrodomésticos': 'MCO1144',
      'refrigeracion': 'MCO1144',
      'refrigeración': 'MCO1144',
      'lavado': 'MCO1144',
      
      // Deportes
      'deportes': 'MCO1276',
      'fitness': 'MCO1276',
      'ejercicio': 'MCO1276',
      
      // Ropa y accesorios
      'ropa': 'MCO1430',
      'accesorios': 'MCO1430',
      'zapatos': 'MCO1430',
      'bolsos': 'MCO1430',
      
      // Construcción y herramientas
      'construccion': 'MCO1499',
      'construcción': 'MCO1499', 
      'herramientas': 'MCO1499',
      'ferreteria': 'MCO1499',
      'ferretería': 'MCO1499',
      
      // Otros
      'otros': 'MCO1367',
      'otras categorias': 'MCO1367',
      'otras categorías': 'MCO1367'
    };

    const normalized = descriptiveCategory.toLowerCase().trim();
    
    // Check for exact matches first
    if (categoryMapping[normalized]) {
      return categoryMapping[normalized];
    }
    
    // Check for partial matches
    for (const [key, categoryId] of Object.entries(categoryMapping)) {
      if (normalized.includes(key) || key.includes(normalized)) {
        return categoryId;
      }
    }
    
    return null;
  }

  /**
   * 🔧 Mapea y corrige automáticamente los datos del producto
   * para evitar errores de tipos de datos y API de MercadoLibre
   */
  mapProductData(product) {
    console.log('🔍 Mapeando datos del producto:', product.titulo);
    
    // 1. Procesar precio - siempre terminar en 99
    let precio = product.precio || 0;
    
    // Si es string, convertir a número
    if (typeof precio === 'string') {
      // Remover símbolos de moneda y espacios
      precio = precio.replace(/[$\s,]/g, '').replace(/\./g, '');
      precio = parseFloat(precio) || 0;
    }
    
    // Asegurar que termine en 99
    if (precio > 0) {
      const precioStr = Math.floor(precio).toString();
      const precioBase = precioStr.slice(0, -2) || '0';
      precio = parseFloat(precioBase + '99');
    }
    
    // 2. Procesar stock - siempre 0
    let stock = 0; // Como dijiste, siempre 0
    
    // 3. Procesar categoría - MEJORADO para evitar errores de API
    let categoria = product.categoria || '';
    if (typeof categoria !== 'string') {
      categoria = String(categoria).trim();
    }
    
    // Mapear categorías problemáticas a categorías válidas
    const categoriaCorregida = this.fixCategory(categoria);
    
    // 4. Procesar título
    let titulo = product.titulo || '';
    if (typeof titulo !== 'string') {
      titulo = String(titulo).trim();
    }
    
    const resultado = {
      titulo: titulo,
      precio: precio,
      stock: stock,
      categoria: categoriaCorregida
    };
    
    console.log('✅ Datos corregidos:', {
      titulo: resultado.titulo,
      precio: `${resultado.precio} (${typeof resultado.precio})`,
      stock: `${resultado.stock} (${typeof resultado.stock})`,
      categoria: `${resultado.categoria} (${typeof resultado.categoria})`
    });
    
    return resultado;
  }

  /**
   * 🔧 Corrige categorías que causan errores en la API de MercadoLibre
   */
  fixCategory(categoria) {
    // Si está vacía o es la problemática MCO1367, usar categorías válidas específicas
    if (!categoria || categoria === 'MCO1367' || categoria.includes('Otros')) {
      // Usar una categoría más específica que funcione mejor
      return 'MCO1430'; // Ropa y accesorios - más permisiva
    }
    
    // Mapear categorías problemáticas a válidas
    const categoriasProblematicas = {
      'Otras categorías > Otros': 'MCO1430', // Ropa y accesorios
      'MCO1367': 'MCO1430', // Cambiar "Otros" por "Ropa y accesorios"
      'Hogar y Muebles > Cocina > Bebidas > Accesorios': 'MCO1574', // Hogar y jardín
      'Iluminación > LED': 'MCO1574', // Hogar y jardín
    };
    
    // Verificar si la categoría es problemática
    for (const [problematica, solucion] of Object.entries(categoriasProblematicas)) {
      if (categoria.includes(problematica) || categoria === problematica) {
        console.log(`🔧 Categoría corregida: ${categoria} → ${solucion}`);
        return solucion;
      }
    }
    
    // Si no es problemática, mantener la original
    return categoria;
  }

  /**
   * 🖼️ Genera URLs de imágenes por defecto para evitar error de "pictures mandatory"
   */
  getDefaultImages() {
    // Imágenes por defecto de placeholder que cumplen los requisitos de MercadoLibre
    return [
      'https://via.placeholder.com/800x600/ffffff/000000?text=Producto',
      'https://via.placeholder.com/800x600/f0f0f0/333333?text=Vista+2',
      'https://via.placeholder.com/800x600/e0e0e0/666666?text=Detalles'
    ];
  }

  // --- Autenticación y tokens ---

  getAccessToken() {
    if (typeof window !== 'undefined') {
      const stored = window?.localStorage?.getItem('mercadolibre_access_token');
      if (stored) return stored;
    }
    return import.meta.env?.VITE_MERCADOLIBRE_ACCESS_TOKEN || null;
  }

  getRefreshToken() {
    if (typeof window !== 'undefined') {
      const stored = window?.localStorage?.getItem('mercadolibre_refresh_token');
      if (stored) return stored;
    }
    return import.meta.env?.VITE_MERCADOLIBRE_REFRESH_TOKEN || null;
  }

  getUserId() {
    if (typeof window !== 'undefined') {
      const stored = window?.localStorage?.getItem('mercadolibre_user_id');
      if (stored) return stored;
    }
    return import.meta.env?.VITE_MERCADOLIBRE_USER_ID || null;
  }

  getTokenExpiry() {
    if (typeof window === 'undefined') return null;
    const raw = window?.localStorage?.getItem('mercadolibre_token_expires_at');
    return raw ? parseInt(raw, 10) : null;
  }

  storeTokens(accessToken, refreshToken, userId, expiresIn) {
    if (typeof window === 'undefined') return;
    if (accessToken) {
      window?.localStorage?.setItem('mercadolibre_access_token', accessToken);
    }
    if (refreshToken !== undefined) {
      if (refreshToken) {
        window?.localStorage?.setItem('mercadolibre_refresh_token', refreshToken);
      } else {
        window?.localStorage?.removeItem('mercadolibre_refresh_token');
      }
    }
    if (userId) {
      window?.localStorage?.setItem('mercadolibre_user_id', String(userId));
    }
    if (expiresIn) {
      const expiry = Date.now() + (expiresIn * 1000);
      window?.localStorage?.setItem('mercadolibre_token_expires_at', expiry.toString());
    }
  }

  clearTokens() {
    if (typeof window === 'undefined') return;
    window?.localStorage?.removeItem('mercadolibre_access_token');
    window?.localStorage?.removeItem('mercadolibre_refresh_token');
    window?.localStorage?.removeItem('mercadolibre_user_id');
    window?.localStorage?.removeItem('mercadolibre_token_expires_at');
  }

  isAuthenticated() {
    const token = this.getAccessToken();
    if (!token) return false;
    const expiry = this.getTokenExpiry();
    if (!expiry || Number.isNaN(expiry)) return true;
    return Date.now() < expiry;
  }

  async refreshAccessToken() {
    try {
      const apiBaseUrl =
        this.apiBaseUrl ||
        (
          typeof window !== 'undefined'
            ? window.location.origin
            : ''
        );

      const userId =
        this.getUserId();

      if (!userId) {
        console.error(
          'No hay seller activo para renovar Mercado Libre'
        );
        return null;
      }

      const {
        data: { session }
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        console.error(
          'La sesión de Mercado Bulk expiró'
        );
        return null;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/mercadolibre/refresh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            userId
          })
        }
      );

      const payload =
        await response
          .json()
          .catch(() => ({}));

      if (
        !response.ok ||
        !payload?.success ||
        !payload?.data
      ) {
        console.error(
          'Error refreshing MercadoLibre token:',
          payload?.message ||
            response.statusText
        );
        return null;
      }

      const {
        access_token,
        user_id,
        expires_in
      } = payload.data;

      if (
        String(user_id || '') !==
        String(userId)
      ) {
        console.error(
          'Mercado Libre devolvió un seller distinto al perfil actual'
        );
        return null;
      }

      /*
       * Refresh token solamente en servidor.
       * El navegador conserva el access token del seller
       * correcto de este perfil.
       */
      this.storeTokens(
        access_token,
        null,
        user_id,
        expires_in
      );

      window?.localStorage?.setItem(
        'mercadolibre_verified_user_id',
        String(user_id)
      );

      return {
        ...payload.data,
        refresh_token: null
      };
    } catch (error) {
      console.error(
        'Unexpected error refreshing MercadoLibre token:',
        error
      );
      return null;
    }
  }
}

// Create and export singleton instance
const mercadoLibreService = new MercadoLibreService();
export default mercadoLibreService;